// Shared copy helpers for the delete-confirm UI used across
// every surface that can delete a note, a follow-up, or a
// deal — the inbox rail's NOTES / NEXT STEPS rows, the
// deal/contact activity feed, the deal Next steps section,
// the Today page (CON-253), and the deal more-menu (CON-256).
// Both the desktop popover (which adds a quote subtitle below
// the question) and the mobile inline / sheet confirm ship the
// same copy — heading + quoted body — so the rep sees what
// they're deleting before tapping the red button.
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react/macro"

const MAX_QUOTE_LENGTH = 80

// Clip a long row title / note excerpt to a single line of
// readable preview text in the confirm UI. Ellipsis lives in
// the string itself (not CSS truncate) because the popover
// can wrap onto multiple short rows otherwise — we want the
// "…" to be visible as a deliberate cue that the value was
// trimmed.
export function quoteForDeletePrompt(raw: string | null | undefined): string {
  const trimmed = (raw ?? "").trim()
  if (trimmed.length === 0) return ""
  if (trimmed.length <= MAX_QUOTE_LENGTH) return trimmed
  return `${trimmed.slice(0, MAX_QUOTE_LENGTH).trimEnd()}…`
}

export type DeletePromptKind = "follow-up" | "note" | "deal"

interface DeletePromptCopy {
  heading: string
  subtitle: string
}

// One source of truth for the heading + quoted subtitle used
// by every delete-confirm surface (desktop popover and the
// mobile inline / sheet confirm). If the empty-quote variant
// ever needs a different fallback ("Delete this follow-up?"
// alone, with no subtitle), callers can detect it via
// `subtitle === ""`. Deleting a deal is a heavier, permanent
// action so its copy spells that out.
export function deletePromptCopy(
  kind: DeletePromptKind,
  quote: string | null | undefined,
  t: ReturnType<typeof useLingui>["t"],
): DeletePromptCopy {
  const heading =
    kind === "follow-up"
      ? t(msg`Delete this follow-up?`)
      : kind === "deal"
        ? t(msg`Delete this deal?`)
        : t(msg`Delete this note?`)

  const quoted = quoteForDeletePrompt(quote)
  let subtitle = ""
  if (kind === "deal") {
    subtitle =
      quoted.length > 0
        ? t(msg`"${quoted}" and its history will be permanently deleted.`)
        : t(msg`This deal and its history will be permanently deleted.`)
  } else if (quoted.length > 0) {
    subtitle = t(msg`"${quoted}" will be removed.`)
  }

  return { heading, subtitle }
}
